import {
  EnvironmentId,
  PullRequestListEntry,
  type PullRequestListResult,
} from "@cmmd-forge/contracts";
import * as Option from "effect/Option";
import * as Schema from "effect/Schema";

const SNAPSHOT_MAX_ENTRIES = 99;

const ScopedWarmEntry = Schema.Struct({
  ...PullRequestListEntry.fields,
  environmentId: EnvironmentId,
});

const WarmSnapshot = Schema.Struct({
  version: Schema.Literal(1),
  environmentKey: Schema.String,
  entries: Schema.Array(ScopedWarmEntry).check(Schema.isMaxLength(SNAPSHOT_MAX_ENTRIES)),
  viewersByEnvironment: Schema.optional(
    Schema.Record(Schema.String, Schema.Record(Schema.String, Schema.String)),
  ),
});
type WarmSnapshot = typeof WarmSnapshot.Type;
const decodeWarmSnapshot = Schema.decodeUnknownOption(WarmSnapshot);

function pullRequestEnvironmentSetKey(environmentIds: ReadonlyArray<EnvironmentId>): string {
  return [...environmentIds].toSorted((left, right) => left.localeCompare(right)).join(",");
}

export function serializePullRequestWarmSnapshot(input: {
  readonly environmentIds: ReadonlyArray<EnvironmentId>;
  readonly entries: ReadonlyArray<
    PullRequestListResult["entries"][number] & { readonly environmentId: EnvironmentId }
  >;
  readonly viewersByEnvironment?: Readonly<Record<string, Readonly<Record<string, string>>>>;
  readonly errors?: unknown;
  readonly nextCursors?: unknown;
}): string {
  return JSON.stringify({
    version: 1,
    environmentKey: pullRequestEnvironmentSetKey(input.environmentIds),
    entries: input.entries.slice(0, SNAPSHOT_MAX_ENTRIES),
    viewersByEnvironment: input.viewersByEnvironment ?? {},
  });
}

export function restorePullRequestWarmSnapshot(
  raw: string,
  environmentIds: ReadonlyArray<EnvironmentId>,
): (WarmSnapshot & { readonly freshness: "stale" }) | null {
  try {
    const decoded = decodeWarmSnapshot(JSON.parse(raw));
    return Option.isSome(decoded) &&
      decoded.value.environmentKey === pullRequestEnvironmentSetKey(environmentIds)
      ? { ...decoded.value, freshness: "stale" }
      : null;
  } catch {
    return null;
  }
}
